import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';

/**
 * Catch-all for any path that matched no route.
 *
 * Also normalizes trailing slashes client-side: a path like "/some/path/"
 * that didn't match a registered route (React Router already matches known
 * routes with or without a trailing slash on its own) redirects to the
 * slash-less version before falling through to a real 404.
 *
 * Note: this is a plain client-rendered SPA with no server code, so this
 * cannot set a true HTTP 404 status — only the noindex meta tag below. A
 * real 404 status for unmatched paths would need a change at the hosting/
 * deploy layer, outside this repo.
 */
export const NotFound = () => {
  const location = useLocation();

  if (location.pathname !== '/' && location.pathname.endsWith('/')) {
    return <Navigate to={location.pathname.slice(0, -1) + location.search + location.hash} replace />;
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center py-24" data-testid="not-found-page">
      <SEOHead title="Page Not Found" canonicalPath={location.pathname} noindex />
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h1 className="text-4xl font-serif text-stone-900 mb-4">Page Not Found</h1>
        <p className="text-stone-600 mb-8">
          The page you're looking for doesn't exist or may have moved.
        </p>
        <Button asChild className="bg-cafa-red hover:bg-cafa-red-900 text-white" data-testid="not-found-home-link">
          <Link to="/">Back to Home</Link>
        </Button>
      </div>
    </div>
  );
};

export default NotFound;
